import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { gradientCssForArtist } from "@/lib/artistTheme";

/**
 * ArtistAvatar — auto-resolves a real profile picture for an artist via the
 * backend's multi-source resolver (Deezer → iTunes → Wikipedia → Gemini).
 *
 * Used by the home page Trending New Artists row + anywhere else we render
 * an artist card without a pre-supplied artwork URL. Falls back to a
 * deterministic gradient + initial when no source returns anything (rare).
 *
 * Props
 * ─────
 *   artist     {object} — must have `artistName`. Optional: `artistId`,
 *                         `displayName`, `picture`, `artworkUrl100`.
 *   className  {string} — extra classes for the wrapping div
 *   children   {node}   — overlay (e.g., rank pill) rendered ABOVE the image
 *
 * The picture is cached server-side for 30 days; the frontend itself
 * holds the resolved URL in component state, so revisiting the same row
 * never re-fetches.
 */
const ArtistAvatar = ({ artist, className = "", children }) => {
  const seed = artist?.artistName || artist?.displayName || "anon";
  const initialPic = artist?.picture || artist?.artworkUrl100 || null;
  const [pic, setPic] = useState(initialPic);
  const [resolving, setResolving] = useState(!initialPic);

  useEffect(() => {
    if (pic) return;
    if (!artist?.artistName && !artist?.displayName) return;
    let cancelled = false;
    setResolving(true);
    api
      .get("/artwork/artist", {
        params: {
          artist_name: artist.artistName || artist.displayName,
          artist_id: artist.artistId,
        },
      })
      .then((r) => {
        if (!cancelled && r.data?.picture) setPic(r.data.picture);
      })
      .catch(() => {})
      .finally(() => !cancelled && setResolving(false));
    return () => { cancelled = true; };
  }, [artist?.artistName, artist?.displayName, artist?.artistId, pic]);

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      style={pic ? undefined : { background: gradientCssForArtist(seed) }}
    >
      {pic ? (
        <img
          src={pic}
          alt={artist?.artistName || artist?.displayName || "Artist"}
          loading="lazy"
          // Treat any image error as if we never had a picture — drop
          // back to ``pic = null`` so the gradient + initial-letter tile
          // takes over via the existing branch below. Same path also
          // re-kicks the resolver (useEffect dep on `pic`), giving the
          // backend a chance to refresh a stale Google avatar URL etc.
          onError={() => setPic(null)}
          className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
        />
      ) : (
        <span
          aria-hidden
          className="absolute inset-0 flex items-center justify-center font-display text-[120px] text-white/85 leading-none -mb-3"
        >
          {(artist?.displayName || artist?.artistName || "?")[0]?.toUpperCase()}
        </span>
      )}
      {/* Subtle shimmer while resolving — only visible during the network
          call so the first-paint placeholder doesn't look broken. */}
      {resolving && !pic && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent animate-[shimmer_1.4s_ease-in-out_infinite]" />
      )}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none" />
      {children}
    </div>
  );
};

export default ArtistAvatar;
